import { useState, useEffect } from 'react'
import { getFamily, addFamilyMember, removeFamilyMember } from '../api/members'
import type { FamilyResponse } from '../api/members'

export function FamilySection() {
  const [family, setFamily] = useState<FamilyResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [addAuid, setAddAuid] = useState('')
  const [adding, setAdding] = useState(false)
  const [removing, setRemoving] = useState<string | null>(null)

  useEffect(() => {
    getFamily()
      .then(setFamily)
      .catch(() => setError('Failed to load family information'))
      .finally(() => setLoading(false))
  }, [])

  async function handleAdd(e: React.FormEvent) {
    e.preventDefault()
    if (!addAuid.trim()) return
    setError('')
    setAdding(true)
    try {
      const updated = await addFamilyMember(addAuid.trim())
      setFamily(updated)
      setAddAuid('')
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error
      setError(msg || 'Failed to add family member')
    } finally {
      setAdding(false)
    }
  }

  async function handleRemove(auid: string) {
    setError('')
    setRemoving(auid)
    try {
      const updated = await removeFamilyMember(auid)
      setFamily(updated)
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error
      setError(msg || 'Failed to remove family member')
    } finally {
      setRemoving(null)
    }
  }

  if (loading) {
    return (
      <div className="text-sm text-text-muted py-2">Loading family information...</div>
    )
  }

  // Not a family member — show option to start a family
  if (!family || !family.isFamily) {
    return (
      <div>
        <h2 className="text-lg font-heading font-semibold text-text mb-3">Family Membership</h2>
        <p className="text-sm text-text-muted mb-4">
          You are currently an individual member. Add a family member below to start a family membership.
        </p>

        {error && (
          <div className="bg-error/10 border border-error/30 text-error rounded-md px-4 py-3 mb-4 text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleAdd} className="flex gap-2 items-end">
          <div className="flex-1">
            <label htmlFor="addAuid" className="block text-sm font-medium text-text mb-1">
              Add by AU ID
            </label>
            <input
              id="addAuid"
              type="text"
              value={addAuid}
              onChange={(e) => setAddAuid(e.target.value)}
              placeholder="e.g. AU1234"
              className="w-full px-3 py-2 border border-border rounded-md bg-surface text-text focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
            />
          </div>
          <button
            type="submit"
            disabled={adding || !addAuid.trim()}
            className="px-4 py-2 bg-primary hover:bg-primary-light text-text-inverse text-sm font-medium rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {adding ? 'Adding...' : 'Add Member'}
          </button>
        </form>
      </div>
    )
  }

  // Part of a family but not the head
  if (!family.isHead) {
    return (
      <div>
        <h2 className="text-lg font-heading font-semibold text-text mb-3">Family Membership</h2>
        <p className="text-sm text-text-muted mb-4">
          You are part of a family membership managed by <span className="font-medium text-text">{family.headAUID}</span>.
          Contact the family head to make changes.
        </p>

        {family.members.length > 0 && (
          <div className="border border-border rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-surface-alt">
                <tr>
                  <th className="text-left px-4 py-2 font-medium text-text-muted">Name</th>
                  <th className="text-left px-4 py-2 font-medium text-text-muted">AU ID</th>
                  <th className="text-left px-4 py-2 font-medium text-text-muted">Class</th>
                </tr>
              </thead>
              <tbody>
                {family.members.map((m) => (
                  <tr key={m.auid} className="border-t border-border">
                    <td className="px-4 py-2 text-text">
                      {m.first} {m.last}
                      {m.auid === family.headAUID && (
                        <span className="ml-2 text-xs text-text-muted">(Head)</span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-text-light">{m.auid}</td>
                    <td className="px-4 py-2 text-text-light">{m.class}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    )
  }

  // Family head — full management
  return (
    <div>
      <h2 className="text-lg font-heading font-semibold text-text mb-3">Family Membership</h2>
      <p className="text-sm text-text-muted mb-4">
        You are the family head. You can add or remove family members below.
      </p>

      {error && (
        <div className="bg-error/10 border border-error/30 text-error rounded-md px-4 py-3 mb-4 text-sm">
          {error}
        </div>
      )}

      {family.members.length > 0 && (
        <div className="border border-border rounded-lg overflow-hidden mb-4">
          <table className="w-full text-sm">
            <thead className="bg-surface-alt">
              <tr>
                <th className="text-left px-4 py-2 font-medium text-text-muted">Name</th>
                <th className="text-left px-4 py-2 font-medium text-text-muted">AU ID</th>
                <th className="text-left px-4 py-2 font-medium text-text-muted">Class</th>
                <th className="text-left px-4 py-2 font-medium text-text-muted">Dues</th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {family.members.map((m) => (
                <tr key={m.auid} className="border-t border-border">
                  <td className="px-4 py-2 text-text">
                    {m.first} {m.last}
                    {m.auid === family.headAUID && (
                      <span className="ml-2 text-xs text-text-muted">(You)</span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-text-light">{m.auid}</td>
                  <td className="px-4 py-2 text-text-light">{m.class}</td>
                  <td className="px-4 py-2">
                    {m.duesPd ? (
                      <span className="text-success text-xs">Current</span>
                    ) : (
                      <span className="text-error text-xs">Past Due</span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-right">
                    {m.auid !== family.headAUID && (
                      <button
                        onClick={() => handleRemove(m.auid)}
                        disabled={removing === m.auid}
                        className="text-xs text-error hover:text-error/80 disabled:opacity-50"
                      >
                        {removing === m.auid ? 'Removing...' : 'Remove'}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <form onSubmit={handleAdd} className="flex gap-2 items-end">
        <div className="flex-1">
          <label htmlFor="addAuid" className="block text-sm font-medium text-text mb-1">
            Add Family Member by AU ID
          </label>
          <input
            id="addAuid"
            type="text"
            value={addAuid}
            onChange={(e) => setAddAuid(e.target.value)}
            placeholder="e.g. AU1234"
            className="w-full px-3 py-2 border border-border rounded-md bg-surface text-text focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
          />
        </div>
        <button
          type="submit"
          disabled={adding || !addAuid.trim()}
          className="px-4 py-2 bg-primary hover:bg-primary-light text-text-inverse text-sm font-medium rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {adding ? 'Adding...' : 'Add Member'}
        </button>
      </form>
    </div>
  )
}
